import { YAHOO_BASE, COINGECKO_BASE, PROXY_BASE, shouldUseDirectApi } from '../config/apiConfig';
import { getAuthHeaders } from './authService';

// ── Direct Yahoo Finance helpers ──────────────────────────────────────────────

async function yahooFetch(path, timeout = 8000) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeout);
  try {
    const res = await fetch(`${YAHOO_BASE}${path}`, {
      signal: controller.signal,
    });
    if (!res.ok) throw new Error(`Yahoo ${res.status}`);
    return res.json();
  } finally {
    clearTimeout(timer);
  }
}

async function fetchAnalystData(symbol) {
  try {
    const data = await yahooFetch(
      `/v10/finance/quoteSummary/${encodeURIComponent(symbol)}?modules=financialData,summaryProfile`,
      6000
    );
    const fd = data?.quoteSummary?.result?.[0]?.financialData;
    const sp = data?.quoteSummary?.result?.[0]?.summaryProfile;
    if (!fd) return null;
    return {
      recommendationMean:      fd.recommendationMean?.raw      ?? null,
      numberOfAnalystOpinions: fd.numberOfAnalystOpinions?.raw ?? null,
      targetMeanPrice:         fd.targetMeanPrice?.raw         ?? null,
      targetLowPrice:          fd.targetLowPrice?.raw          ?? null,
      targetHighPrice:         fd.targetHighPrice?.raw         ?? null,
      recommendationKey:       fd.recommendationKey            ?? null,
      sector:                  sp?.sector                      ?? null,
      industry:                sp?.industry                    ?? null,
    };
  } catch {
    return null;
  }
}

// ── Direct: Enriched stock data (chart + analyst) ─────────────────────────────

async function fetchEnrichedStockDirect(sym) {
  try {
    const [chartRes, analystRes] = await Promise.allSettled([
      yahooFetch(`/v8/finance/chart/${encodeURIComponent(sym)}?range=1mo&interval=1d`),
      fetchAnalystData(sym),
    ]);

    if (chartRes.status !== 'fulfilled') return null;
    const result = chartRes.value?.chart?.result?.[0];
    if (!result) return null;

    const meta    = result.meta;
    const closes  = (result.indicators?.quote?.[0]?.close  || []).filter(Boolean);
    const volumes = (result.indicators?.quote?.[0]?.volume || []).filter(Boolean);

    const current   = meta.regularMarketPrice;
    const prevClose = meta.chartPreviousClose;
    const change24h = prevClose > 0 ? ((current - prevClose) / prevClose) * 100 : 0;
    const idx7d     = Math.max(0, closes.length - 6);
    const close7dAgo = closes[idx7d] || closes[0] || prevClose;
    const change7d  = close7dAgo > 0 ? ((current - close7dAgo) / close7dAgo) * 100 : 0;

    const analyst = analystRes.status === 'fulfilled' ? analystRes.value : null;

    return {
      ticker:                  sym.toUpperCase(),
      price:                   current,
      currency:                'USD',
      change24h:               parseFloat(change24h.toFixed(2)),
      change7d:                parseFloat(change7d.toFixed(2)),
      closes,
      avgVolume:               volumes.length > 5 ? volumes.slice(0, -1).reduce((s, v) => s + v, 0) / (volumes.length - 1) : null,
      lastVolume:              volumes[volumes.length - 1] || null,
      week52Low:               meta.fiftyTwoWeekLow  || null,
      week52High:              meta.fiftyTwoWeekHigh || null,
      name:                    meta.longName || meta.shortName || sym,
      source:                  'yahoo',
      recommendationMean:      analyst?.recommendationMean      ?? null,
      numberOfAnalystOpinions: analyst?.numberOfAnalystOpinions ?? null,
      targetMeanPrice:         analyst?.targetMeanPrice         ?? null,
      recommendationKey:       analyst?.recommendationKey       ?? null,
      sector:                  analyst?.sector                  ?? null,
      industry:                analyst?.industry                ?? null,
    };
  } catch {
    return null;
  }
}

// ── Stock Prices ──────────────────────────────────────────────────────────────

async function fetchStockPricesDirect(symbols) {
  const results = await Promise.allSettled(symbols.map(fetchEnrichedStockDirect));
  const out = {};
  results.forEach(r => {
    if (r.status === 'fulfilled' && r.value) out[r.value.ticker] = r.value;
  });
  return out;
}

async function fetchStockPricesProxy(symbols) {
  const res = await fetch(`${PROXY_BASE}/api/prices/stocks?symbols=${symbols.join(',')}`);
  if (!res.ok) throw new Error(`stocks fetch failed: ${res.status}`);
  return res.json();
}

export async function fetchStockPrices(symbols) {
  if (!symbols.length) return {};
  return shouldUseDirectApi() ? fetchStockPricesDirect(symbols) : fetchStockPricesProxy(symbols);
}

// ── Crypto Prices ─────────────────────────────────────────────────────────────

async function fetchCryptoPricesDirect(ids) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 8000);
  try {
    const res = await fetch(
      `${COINGECKO_BASE}/coins/markets?vs_currency=eur&ids=${ids.join(',')}&price_change_percentage=7d&per_page=50`,
      { signal: controller.signal }
    );
    if (!res.ok) throw new Error(`CoinGecko ${res.status}`);
    const data = await res.json();
    const prices = {};
    data.forEach(coin => {
      prices[coin.symbol.toUpperCase()] = {
        ticker:    coin.symbol.toUpperCase(),
        price:     coin.current_price,
        currency:  'EUR',
        change24h: parseFloat((coin.price_change_percentage_24h || 0).toFixed(2)),
        change7d:  parseFloat((coin.price_change_percentage_7d_in_currency || 0).toFixed(2)),
        name:      coin.name,
        source:    'coingecko',
      };
    });
    return prices;
  } finally {
    clearTimeout(timer);
  }
}

async function fetchCryptoPricesProxy(ids) {
  const res = await fetch(`${PROXY_BASE}/api/prices/crypto?ids=${ids.join(',')}`);
  if (!res.ok) throw new Error(`crypto fetch failed: ${res.status}`);
  return res.json();
}

export async function fetchCryptoPrices(ids) {
  if (!ids.length) return {};
  return shouldUseDirectApi() ? fetchCryptoPricesDirect(ids) : fetchCryptoPricesProxy(ids);
}

// ── EUR/USD Rate ──────────────────────────────────────────────────────────────

async function fetchEurUsdDirect() {
  try {
    const data = await yahooFetch('/v8/finance/chart/EURUSD=X?range=1d&interval=1d', 6000);
    return data?.chart?.result?.[0]?.meta?.regularMarketPrice || 1.08;
  } catch {
    return 1.08;
  }
}

async function fetchEurUsdProxy() {
  try {
    const res = await fetch(`${PROXY_BASE}/api/forex/eurusd`);
    const data = await res.json();
    return data.rate || 1.08;
  } catch {
    return 1.08;
  }
}

export async function fetchEurUsdRate() {
  return shouldUseDirectApi() ? fetchEurUsdDirect() : fetchEurUsdProxy();
}

// ── Chart Data ────────────────────────────────────────────────────────────────

// range → Yahoo Finance range + interval
const RANGE_MAP = {
  '7T':  { range: '7d',   interval: '1h' },
  '1M':  { range: '1mo',  interval: '1d' },
  '6M':  { range: '6mo',  interval: '1d' },
  '1J':  { range: '1y',   interval: '1wk' },
  '5J':  { range: '5y',   interval: '1mo' },
};

async function fetchChartDirect(ticker, period = '1M') {
  const { range, interval } = RANGE_MAP[period] || RANGE_MAP['1M'];
  const data = await yahooFetch(
    `/v8/finance/chart/${encodeURIComponent(ticker.toUpperCase())}?range=${range}&interval=${interval}`
  );
  const result = data?.chart?.result?.[0];
  if (!result) return { ticker, points: [] };
  const timestamps = result.timestamp || [];
  const closes = result.indicators?.quote?.[0]?.close || [];
  const points = timestamps
    .map((ts, i) => ({ date: new Date(ts * 1000).toISOString().slice(0, 10), close: closes[i] }))
    .filter(p => p.close != null);
  return { ticker: ticker.toUpperCase(), points };
}

async function fetchChartProxy(ticker, period = '1M') {
  const { range, interval } = RANGE_MAP[period] || RANGE_MAP['1M'];
  const res = await fetch(`${PROXY_BASE}/api/chart/${ticker}?range=${range}&interval=${interval}`);
  if (!res.ok) throw new Error(`chart ${res.status}`);
  return res.json();
}

export async function fetchChart(ticker, period = '1M') {
  return shouldUseDirectApi() ? fetchChartDirect(ticker, period) : fetchChartProxy(ticker, period);
}

// ── News ──────────────────────────────────────────────────────────────────────

async function fetchNewsDirect(ticker) {
  try {
    const data = await yahooFetch(
      `/v1/finance/search?q=${encodeURIComponent(ticker.toUpperCase())}&quotesCount=0&newsCount=5&enableFuzzyQuery=false`,
      6000
    );
    return (data?.news || []).slice(0, 5).map(n => ({
      title:     n.title,
      publisher: n.publisher,
      link:      n.link,
      time:      n.providerPublishTime,
    }));
  } catch {
    return [];
  }
}

async function fetchNewsProxy(ticker) {
  try {
    const res = await fetch(`${PROXY_BASE}/api/news/${ticker}`);
    if (!res.ok) return [];
    return res.json();
  } catch {
    return [];
  }
}

export async function fetchNews(ticker) {
  return shouldUseDirectApi() ? fetchNewsDirect(ticker) : fetchNewsProxy(ticker);
}

// ── Search ────────────────────────────────────────────────────────────────────

async function fetchSearchDirect(query) {
  const data = await yahooFetch(
    `/v1/finance/search?q=${encodeURIComponent(query)}&quotesCount=8&newsCount=0&enableFuzzyQuery=true`,
    6000
  );
  return (data?.quotes || [])
    .filter(item => ['EQUITY', 'ETF', 'CRYPTOCURRENCY'].includes(item.quoteType))
    .map(item => ({
      ticker:   item.symbol,
      name:     item.longname || item.shortname || item.symbol,
      type:     item.quoteType === 'CRYPTOCURRENCY' ? 'crypto' : 'stock',
      exchange: item.exchDisp || item.exchange || '',
      sector:   item.sectorDisp || item.sector || '',
      industry: item.industryDisp || item.industry || '',
    }));
}

async function fetchSearchProxy(query) {
  const res = await fetch(`${PROXY_BASE}/api/search?q=${encodeURIComponent(query)}`);
  if (!res.ok) throw new Error(`search ${res.status}`);
  return res.json();
}

export async function fetchSearch(query) {
  if (!query.trim()) return [];
  const results = await (shouldUseDirectApi() ? fetchSearchDirect(query) : fetchSearchProxy(query));
  const seenNames = new Set();
  return results.filter(item => {
    const name = (item.name || '').toLowerCase().trim();
    if (!name) return true;
    if (seenNames.has(name)) return false;
    seenNames.add(name);
    return true;
  });
}

// ── Fundamentaldaten ──────────────────────────────────────────────────────────

export async function fetchFundamentals(ticker) {
  try {
    const res = await fetch(`${PROXY_BASE}/api/fundamentals/${encodeURIComponent(ticker)}`);
    if (!res.ok) return null;
    return res.json();
  } catch { return null; }
}

// ── Discover – kuratierte Liste (Trade Republic / finanzen.net zero handelbar) ─

const CURATED_STOCKS = [
  // ── US – Mega Cap / Magnificent 7 ────────────────────────────────────────────
  'AAPL', 'MSFT', 'NVDA', 'GOOGL', 'AMZN', 'META', 'TSLA', 'AVGO',

  // ── US – Tech ────────────────────────────────────────────────────────────────
  'NFLX', 'AMD', 'INTC', 'QCOM', 'ORCL', 'CRM', 'ADBE', 'INTU',
  'NOW', 'PANW', 'CRWD', 'PLTR', 'SNOW', 'DDOG', 'FTNT', 'ZS',
  'AMAT', 'LRCX', 'MU', 'TXN', 'IBM', 'CSCO', 'UBER', 'SHOP',
  'PYPL', 'TTD', 'COIN', 'APP', 'SMCI',

  // ── US – Finance ─────────────────────────────────────────────────────────────
  'JPM', 'BAC', 'GS', 'MS', 'WFC', 'V', 'MA', 'AXP', 'BRK-B',
  'BLK', 'SCHW', 'COF', 'SPGI', 'MCO', 'BX', 'KKR',

  // ── US – Healthcare ──────────────────────────────────────────────────────────
  'LLY', 'JNJ', 'ABBV', 'UNH', 'AMGN', 'MRK', 'PFE', 'MRNA',
  'GILD', 'VRTX', 'REGN', 'BMY', 'ISRG', 'TMO', 'ABT', 'MDT',

  // ── US – Consumer Discretionary ──────────────────────────────────────────────
  'HD', 'MCD', 'NKE', 'SBUX', 'TJX', 'LOW', 'BKNG',
  'CMG', 'ABNB', 'DIS', 'LULU', 'TSCO', 'EBAY', 'YUM', 'DECK',

  // ── US – Consumer Staples ────────────────────────────────────────────────────
  'WMT', 'KO', 'PG', 'PEP', 'COST', 'PM', 'CL', 'MO', 'MDLZ', 'TGT',

  // ── US – Energie ─────────────────────────────────────────────────────────────
  'XOM', 'CVX', 'COP', 'SLB', 'OXY', 'VLO', 'HAL', 'EOG',

  // ── US – Industrials ─────────────────────────────────────────────────────────
  'GE', 'CAT', 'RTX', 'DE', 'BA', 'HON', 'ETN', 'LMT', 'GD',
  'NOC', 'UPS', 'UNP', 'MMM', 'ADP', 'CTAS',

  // ── US – Kommunikation ───────────────────────────────────────────────────────
  'TMUS', 'VZ', 'T', 'CMCSA', 'EA', 'TTWO',

  // ── Deutschland – DAX 40 (komplett) ─────────────────────────────────────────
  'SIE.DE', 'ALV.DE', 'DTE.DE', 'MUV2.DE', 'MBG.DE',
  'BMW.DE', 'BAYN.DE', 'BAS.DE', 'ADS.DE', 'DBK.DE', 'IFX.DE',
  'RWE.DE', 'VOW3.DE', 'P911.DE', 'SHL.DE', 'ENR.DE', 'HEI.DE',
  'MTX.DE', 'RHM.DE', 'DHL.DE', 'EOAN.DE', 'FRE.DE', 'FME.DE',
  'ZAL.DE', 'VNA.DE', 'HNR1.DE', 'MRK.DE', 'CON.DE', 'BEI.DE',
  'AIR.DE', 'CBK.DE', 'DTG.DE', 'DB1.DE', 'HEN3.DE', 'BNR.DE',
  'SY1.DE', 'QGEN.DE', 'G24.DE', 'PAH3.DE',

  // ── Deutschland – MDAX / TecDAX (bekannte Werte) ─────────────────────────────
  'LHA.DE',   // Lufthansa
  'PUM.DE',   // Puma
  'DHER.DE',  // Delivery Hero
  'SRT3.DE',  // Sartorius
  'NEM.DE',   // Nemetschek
  'AIXA.DE',  // AIXTRON
  'EVT.DE',   // Evotec
  'AFX.DE',   // Carl Zeiss Meditec
  'EVD.DE',   // CTS Eventim
  'FRA.DE',   // Fraport
  'HFG.DE',   // HelloFresh
  'BC8.DE',   // Bechtle
  'LEG.DE',   // LEG Immobilien
  'BOSS.DE',  // Hugo Boss
  'UTDI.DE',  // United Internet
  'TKA.DE',   // ThyssenKrupp
  'NDX1.DE',  // Nordex
  'WAF.DE',   // Siltronic
  'SHA.DE',   // Schaeffler
  'JEN.DE',   // Jenoptik

  // ── Europa ───────────────────────────────────────────────────────────────────
  'SHELL.AS', // Shell
  'MC.PA',    // LVMH
  'OR.PA',    // L'Oréal
  'TTE.PA',   // TotalEnergies
  'SAN.PA',   // Sanofi
  'BNP.PA',   // BNP Paribas
  'NESN.SW',  // Nestlé
  'NOVN.SW',  // Novartis
  'ROG.SW',   // Roche

  // ── Japan (US ADR / US-gelistet) ─────────────────────────────────────────────
  'TM',       // Toyota
  'HMC',      // Honda
  'NTDOY',    // Nintendo
  'SONY',     // Sony

  // ── Sonstige International ───────────────────────────────────────────────────
  'BABA', 'TSM', 'NVO', 'ASML', 'SAP',
];

async function fetchDiscoverDirect() {
  return fetchStockPricesDirect(CURATED_STOCKS);
}

async function fetchDiscoverProxy() {
  const res = await fetch(`${PROXY_BASE}/api/discover`, { headers: await getAuthHeaders() });
  if (!res.ok) throw new Error(`discover ${res.status}`);
  return res.json();
}

export async function fetchDiscover() {
  return shouldUseDirectApi() ? fetchDiscoverDirect() : fetchDiscoverProxy();
}
